import {
  StrategyListItem,
  AdgroupConfig,
  AdgroupFormData,
  CampaignFormData,
  AreaMap,
  CreateMode,
} from '@tt4b/ttam-enums';
import {
  useAllowlist,
  useStdObjectiveType,
  useBidCapSuggestBidAbTestApi,
  useBidCapSuggestBidApi,
  useSuggestBidApi,
  useStore,
  StateEnum,
  useRequest,
  useStdAdgroupOptimizationGoal,
  useStdAdgroupCbo,
} from '@tt4b/ttam-hooks';
import { isEqual, pick } from 'lodash-es';
import { ref, Ref, computed, ComputedRef, watch } from '@vue/composition-api';
import { useCurrencyOption } from './../../../../hooks/creation/account/useCurrencyOption/index';
import {
  localizeCurrency,
  transformCountryToRecordArr,
  SuggestPriceParams,
  SuggestPriceFormDataParams,
} from '@tt4b/ttam-utils';
import { trackEvent } from './../../../../utils/index';
import { BidcapSuggestBidData } from '../types';

function useCommonIsBidcapSuggestBidAvailable(props: { campaignData: Ref<CampaignFormData> }) {
  // 目前Reach下才有啊
  return computed(() => false);
}

/** BidcapSuggestBid  */
export function useBidcapSuggestBidPropsVisible(props: {
  adgroupConfig: Ref<AdgroupConfig>;
  campaignData: Ref<CampaignFormData>;
  bidcapSuggestBidData: Ref<BidcapSuggestBidData>;
}) {
  const { bidcapSuggestBidData, adgroupConfig, campaignData } = props;
  const isBidcapSuggestBidAvailable = useCommonIsBidcapSuggestBidAvailable({
    campaignData,
  });

  return {
    visible: computed(
      () =>
        isBidcapSuggestBidAvailable.value &&
        adgroupConfig.value?.bidcapSuggestBidVisible &&
        bidcapSuggestBidData?.value?.suggestBidNum > 0,
    ),
  };
}

const margin = ref<number | undefined>();
const baseSuggestBid = ref(0);
const isInBidcapSuggestBidTest = ref(false);
export function useBidcapSuggestBidDataSource(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;
  const { state } = useStore();
  const areaData = state[StateEnum.areaData].data;
  const areaMap = computed(() => areaData?.value?.areaMap ?? {});
  const isBidcapSuggestBidAvailable = useCommonIsBidcapSuggestBidAvailable({
    campaignData,
  });
  const isLoading = ref(false);
  const error = ref(null);

  const { currencyOption } = useCurrencyOption();

  const suggestBidNum = computed(() => baseSuggestBid.value * (1 + (margin.value ?? 0) / 100) ?? 0);

  const suggestBidStr = computed(() =>
    localizeCurrency(
      suggestBidNum.value,
      currencyOption.value?.currency,
      currencyOption.value?.langCode,
      currencyOption.value?.country,
      currencyOption.value?.precision,
    ),
  );

  function loadBidcapSuggestBidAbTest() {
    return useBidCapSuggestBidAbTestApi().then(data => {
      margin.value = data.margin;
      isInBidcapSuggestBidTest.value = data.isTest;

      return data;
    });
  }

  function loadBidcapSuggestBid() {
    if (!isBidcapSuggestBidAvailable.value || !isInBidcapSuggestBidTest.value) {
      return;
    }
    const { countryArr } = transformCountryToRecordArr({
      areaMap: areaMap.value,
      particleLocations: adgroupData.value?.particleLocations,
      includeArea: true,
    });
    const countryCodes = countryArr.map(item => item?.code ?? '');
    if (!countryCodes.length) {
      baseSuggestBid.value = 0;

      return;
    }
    const aadvid = computed(() => state[StateEnum.baseInfo].value.aadvid);
    const param = {
      countryCodes,
      aadvid: aadvid.value,
      objectiveType: adgroupData.value?.objectiveType,
      optimizeGoal: adgroupData.value?.optimizeGoal,
    };

    return useBidCapSuggestBidApi(param).then(data => {
      if (data === undefined) {
        return;
      }
      baseSuggestBid.value = data;
    });
  }

  const data: ComputedRef<BidcapSuggestBidData> = computed(() => ({
    margin: margin.value,
    baseSuggestBid: baseSuggestBid.value,
    isInBidcapSuggestBidTest: isInBidcapSuggestBidTest.value,
    suggestBidStr: suggestBidStr.value,
    suggestBidNum: suggestBidNum.value,
  }));

  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  async function run() {
    try {
      if (isCBO.value || !isBidcapSuggestBidAvailable.value) {
        return;
      }
      isLoading.value = true;
      if (!isInBidcapSuggestBidTest.value) {
        await loadBidcapSuggestBidAbTest();
        trackEvent('bidcap_suggest_bid_test_result', { margin: data.value?.margin });
      }
      if (isInBidcapSuggestBidTest.value) {
        await loadBidcapSuggestBid();
      }
      isLoading.value = false;
      error.value = null;
    } catch (err) {
      error.value = err;
    }
  }

  return {
    data,
    isLoading,
    run,
    isEmpty: computed(() => Boolean(data.value)),
    error,
  };
}

const neccssaryParams = ['campaignId', 'pricing', 'currencyCode', 'inventoryFlow', 'classify', 'budget', 'country'];
const judgeParamsMatch = (_params: SuggestPriceParams): boolean =>
  neccssaryParams.every(key => {
    const curVal = _params[key];

    if (Array.isArray(curVal)) {
      return curVal.length;
    }

    if (!isNaN(Number(curVal))) {
      return Boolean(Number(curVal));
    }

    return Boolean(curVal);
  });

// eslint-disable-next-line max-lines-per-function
export function useSuggestBidDataSource(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  curStrategyItem: Ref<StrategyListItem>;
  createMode: Ref<CreateMode>;
  areaMap: Ref<AreaMap>;
  adgroupConfig: Ref<AdgroupConfig>;
}) {
  const { adgroupData, campaignData, curStrategyItem, areaMap, createMode, adgroupConfig } = props;
  const createModeNum = {
    [CreateMode.CREATE]: 1,
    [CreateMode.EDIT]: 2,
    [CreateMode.COPY]: 3,
  };

  const visible = ref(false);

  const { currencyOption } = useCurrencyOption();
  const { isProductSales } = useStdObjectiveType({
    campaignFormData: campaignData,
  });
  const { isOptGoalValue } = useStdAdgroupOptimizationGoal({
    adgroupFormData: adgroupData,
  });

  const { isBbInWhite, isBvInWhite } = useAllowlist();

  // TODO 待split test支持
  const isSplitTestBidding = ref(false);

  const isSupportSuggestPrice = computed(
    () =>
      adgroupConfig.value.showSuggestPrice && // init config
      !isProductSales.value && // objective
      curStrategyItem.value.showSuggestPrice && // bid type
      !isOptGoalValue.value && // optimize goal
      !isSplitTestBidding.value,
  );

  const requestParams: Ref<SuggestPriceParams> = computed(() => {
    const countryIds = adgroupData.value.particleLocations.filter(item => areaMap.value?.[item]?.level === 1);

    return {
      ...pick(adgroupData.value, SuggestPriceFormDataParams),
      createMode: createModeNum[createMode.value],
      currencyCode: currencyOption.value.currency,
      objectiveType: campaignData.value.objectiveType,
      bidPhase: curStrategyItem.value?.bidPhase,
      campaignId: campaignData.value.fakeCampaignId || campaignData.value.campaignId || '',
      country: countryIds,
    };
  });
  const { data: originData, isLoading, error, run: _run, cancel } = useRequest(
    useSuggestBidApi,
    {
      adgroupFormData: adgroupData.value,
      params: requestParams.value,
      isBbInWhite: isBbInWhite.value,
      isBvInWhite: isBvInWhite.value,
    },
    { manual: true },
  );

  const data = computed(() => {
    const suggestBidNum = Number(originData.value?.suggest_bid_i18n || 0).toFixed(currencyOption.value?.precision);
    return {
      ...originData.value,
      visible: curStrategyItem?.value && isSupportSuggestPrice?.value && visible.value,
      suggestBidNum,
      suggestBidStr: localizeCurrency(
        suggestBidNum,
        currencyOption.value?.currency,
        currencyOption.value?.langCode,
        currencyOption.value?.country,
        currencyOption.value?.precision,
      ),
    };
  });

  const run = async (val?: any) => {
    if (!curStrategyItem?.value) {
      visible.value = false;
      return;
    }

    if (!isSupportSuggestPrice.value) {
      visible.value = false;
      return false;
    }

    const params = val || requestParams.value;

    const isParamsMatch = Array.isArray(params) ? params.every(judgeParamsMatch) : judgeParamsMatch(params);

    if (isParamsMatch) {
      try {
        await _run({
          adgroupFormData: adgroupData.value,
          params,
          isBbInWhite: isBbInWhite.value,
          isBvInWhite: isBvInWhite.value,
        });

        if (data?.value?.ad_detail_type >= 0) {
          visible.value = true;
        } else {
          visible.value = false;
        }
      } catch (err) {
        visible.value = false;
      }
    } else {
      visible.value = false;
    }
  };

  watch(requestParams, (newVal, oldVal) => {
    if (isEqual(newVal, oldVal)) {
      return;
    }
    // 应对重复切换导致同时多请求的场景，先cancel再重新请求
    cancel();
    run(newVal);
  });

  return {
    isLoading,
    error,
    run,
    data,
  };
}
